<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-bullhorn"></i> {{ campaign.id ? _('Campaign') + ' ' + campaign.id : _('New campaign') }}</h3>
        </div>
      </div>
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('fi')" class="tab-pane active" id="fi" v-if="isTabActive('fi')" key="fi">
            <div class="row">
              <div class="col-xs-12">
                <template v-if="!isFeed">
                  <div class="form-group">
                    <label for="campaignTitleFi">{{ _('Title') }}</label>
                    <input class="form-control" id="campaignTitleFi" type="text" v-model="campaign.title_fi" />
                  </div>
                  <div class="form-group">
                    <label>{{ _('Text') }}</label>
                    <editor v-model="campaign.text_fi" />
                  </div>
                  <div class="form-group">
                    <label for="ctaTitleFi">{{ _('Button title') }}</label>
                    <input class="form-control" id="ctaTitleFi" type="text" v-model="campaign.cta_title_fi" />
                  </div>
                </template>
                <div class="form-group">
                  <label for="ctaUrlFi">{{ isFeed ? _('Product URL path') : _('URL') }}</label>
                  <input class="form-control" id="ctaUrlFi" type="text" v-model="campaign.cta_url_fi" :placeholder="'https://'" />
                  <p class="help-block" v-if="isFeed">{{ _('The product direct URL is added to the end of this path. If the path ends with ?preview=, the product opens in the page preview popup.') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('en')" class="tab-pane active" id="en" v-if="isTabActive('en')" key="en">
            <div class="row">
              <div class="col-xs-12">
                <template v-if="!isFeed">
                  <div class="form-group">
                    <label for="campaignTitleEn">{{ _('Title') }}</label>
                    <input class="form-control" id="campaignTitleEn" type="text" v-model="campaign.title_en" />
                  </div>
                  <div class="form-group">
                    <label>{{ _('Text') }}</label>
                    <editor v-model="campaign.text_en" />
                  </div>
                  <div class="form-group">
                    <label for="ctaTitleEn">{{ _('Button title') }}</label>
                    <input class="form-control" id="ctaTitleEn" type="text" v-model="campaign.cta_title_en" />
                  </div>
                </template>
                <div class="form-group">
                  <label for="ctaUrlEn">{{ isFeed ? _('Product URL path') : _('URL') }}</label>
                  <input class="form-control" id="ctaUrlEn" type="text" v-model="campaign.cta_url_en" :placeholder="'https://'" />
                  <p class="help-block" v-if="isFeed">{{ _('The product direct URL is added to the end of this path. If the path ends with ?preview=, the product opens in the page preview popup.') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-sm-6 leftcol">
                <div class="form-group">
                  <label for="campaignType">{{ _('Campaign type') }}</label>
                  <select class="form-control" id="campaignType" v-model="campaign.type">
                    <option value="manual">{{ _('Manual campaign') }}</option>
                    <option value="categoryfeed">{{ _('Newest product in a category') }}</option>
                  </select>
                </div>
                <template v-if="isFeed">
                  <div class="form-group">
                    <label for="campaignSourceShop">{{ _('Shop') }}</label>
                    <input class="form-control" id="campaignSourceShop" type="text" v-model.trim="campaign.source_shopid" :placeholder="_('Shop ID')" />
                  </div>
                  <div class="form-group">
                    <label for="campaignSourceCategory">{{ _('Category') }}</label>
                    <select class="form-control" id="campaignSourceCategory" v-model.number="campaign.source_category_id" :disabled="!sourceCategories.length">
                      <option :value="null">{{ categoriesLoading ? _('Loading...') : _('Select a category') }}</option>
                      <option v-for="category in sourceCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
                    </select>
                  </div>
                </template>
                <div class="form-group campaign-published">
                  <label for="campaignPublished">
                    <input id="campaignPublished" type="checkbox" v-model="campaign.published" :true-value="1" :false-value="0" />
                    <strong>{{ _('Published') }}</strong>
                  </label>
                </div>
                <div class="form-group campaign-datepicker">
                  <DatePicker v-model="startDate" :shortLocale="shortLocale" :title="_('Start date')" :placeholder="_('No start date')" :staticInput="true" :clearable="true" field="campaignstart"></DatePicker>
                </div>
                <div class="form-group campaign-datepicker">
                  <DatePicker v-model="endDate" :shortLocale="shortLocale" :title="_('End date')" :placeholder="_('No end date')" :staticInput="true" :clearable="true" field="campaignend"></DatePicker>
                </div>
                <p class="help-block" v-if="isFeed">{{ _('Only products published during this period are shown.') }}</p>
                <div class="form-group">
                  <label for="campaignFeatureFlag">{{ _('Target shops by feature flag') }}</label>
                  <input class="form-control" id="campaignFeatureFlag" type="text" v-model.trim="campaign.feature_flag" :placeholder="_('All shops')" />
                  <select class="form-control campaign-flag-mode" v-model.number="campaign.feature_flag_exclude" v-if="campaign.feature_flag">
                    <option :value="0">{{ _('Only shops with this feature flag') }}</option>
                    <option :value="1">{{ _('Only shops without this feature flag') }}</option>
                  </select>
                </div>
                <div>
                  <h4>{{ _('Targeting') }}</h4>
                  <div class="form-group">
                    <label>{{ _('Show only in these shops') }}</label>
                    <p class="help-block" v-if="!shopsLoaded">{{ _('Loading...') }}</p>
                    <draggable-multi-selector v-else :key="'targetShops-' + campaign.targets.exclude_shops.join(',')" v-model="targetShops" :items="includeShopItems" :search="true" :dragging-enabled="false" :noItemsText="_('All shops')" />
                  </div>
                  <div class="form-group">
                    <label>{{ _('Never show in these shops') }}</label>
                    <p class="help-block" v-if="!shopsLoaded">{{ _('Loading...') }}</p>
                    <draggable-multi-selector v-else :key="'excludeShops-' + campaign.targets.shops.join(',')" v-model="excludeShops" :items="excludeShopItems" :search="true" :dragging-enabled="false" />
                  </div>
                  <div class="form-group" :class="{ 'has-warning': invalidTargetEmails.length }">
                    <label for="campaignTargetEmails">{{ _('Only for these users') }}</label>
                    <textarea class="form-control" id="campaignTargetEmails" rows="4" v-model="targetEmailsText" :placeholder="'user@example.com\n@example.com'"></textarea>
                    <p class="help-block" v-if="invalidTargetEmails.length">{{ _('Not an email address or @domain:') }} {{ invalidTargetEmails.join(', ') }}</p>
                    <p class="help-block">{{ _('One email address or @domain per line. Leave empty to show to all users.') }}</p>
                  </div>
                  <p class="help-block">{{ _('All targeting rules apply together with the feature flag, user level and dates.') }}</p>
                </div>
                <div class="form-group" v-if="campaign.id">
                  <label>{{ _('Campaign ID') }}</label>
                  <input class="form-control" type="text" :value="campaign.id" disabled="disabled" />
                </div>
                <div class="form-group">
                  <label for="campaignMinUserlevel">{{ _('User level') }}</label>
                  <!-- JDEV-3323: one select instead of a checkbox. A stored null still means 7 (admins), so the default is shown, not hidden -->
                  <select class="form-control" id="campaignMinUserlevel" v-model.number="campaign.min_userlevel">
                    <option :value="NO_LEVEL_LIMIT">{{ _('No limit') }}</option>
                    <option v-if="customLevel" :value="campaign.min_userlevel">{{ campaign.min_userlevel }}</option>
                    <option :value="2">2 - {{ _('POS user') }}</option>
                    <option :value="3">3 - {{ _('Standard user') }}</option>
                    <option :value="7">7 - {{ _('Administrator') }}</option>
                    <option :value="10">10 - {{ _('System Administrator') }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-6 rightcol">
                <div class="form-group" v-if="!isFeed">
                  <label>{{ _('Image') }}</label>
                  <div v-if="imagePreviewUrl" class="campaign-image-preview">
                    <img :src="imagePreviewUrl" alt="" />
                  </div>
                  <input type="file" accept="image/*" @change="onImageSelected" />
                  <p class="help-block" v-if="!campaign.id">{{ _('The image is uploaded when the campaign is saved.') }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="flex-row">
          <button class="btn btn-info" type="button" v-on:click="preview" :disabled="loading"><i class="fa fa-fw fa-eye"></i> {{ _('Preview') }}</button>
          <button class="btn btn-danger" type="button" v-on:click="removeCampaign" v-if="campaign.id" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        </div>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import { DatePicker, DraggableMultiSelector, Editor } from '#components'
import debounce from 'lodash/debounce'
import CampaignMobilepay from '~/components/modals/CampaignMobilepay.vue'

// JDEV-3323: "No limit" is stored as 0, which every user level passes. null stays the old default, admins (7).
const NO_LEVEL_LIMIT = 0
const DEFAULT_LEVEL = 7
const LEVEL_OPTIONS = [NO_LEVEL_LIMIT, 2, 3, 7, 10]

export default {
  mixins: [changewatcher, components],
  data () {
    return {
      activeTab: 'fi',
      NO_LEVEL_LIMIT,
      pendingImage: null,
      pendingImageUrl: '',
      sourceCategories: [],
      categoriesLoading: false,
      shops: [],
      shopsLoaded: false,
      targetEmailsText: '',
      campaign: this.emptyCampaign()
    }
  },
  computed: {
    customLevel () {
      return !LEVEL_OPTIONS.includes(this.campaign.min_userlevel)
    },
    tabList () {
      return this.editorTabs([
        { id: 'fi', label: this._('Finnish') },
        { id: 'en', label: this._('English') },
        { id: 'general', label: this._('General') }
      ])
    },
    isFeed () {
      return this.campaign.type === 'categoryfeed'
    },
    startDate: {
      get () {
        if (!this.campaign.start_ts) {
          return null
        }
        return DateTime.fromSeconds(Number(this.campaign.start_ts), { zone: this.timezone }).toJSDate()
      },
      set (newValue) {
        this.campaign.start_ts = newValue
          ? DateTime.fromJSDate(newValue, { zone: this.timezone }).startOf('day').toUnixInteger()
          : null
      }
    },
    endDate: {
      get () {
        if (!this.campaign.end_ts) {
          return null
        }
        return DateTime.fromSeconds(Number(this.campaign.end_ts), { zone: this.timezone }).toJSDate()
      },
      set (newValue) {
        this.campaign.end_ts = newValue
          ? DateTime.fromJSDate(newValue, { zone: this.timezone }).endOf('day').toUnixInteger()
          : null
      }
    },
    previewLocale () {
      return this.isTabActive('en') ? 'en' : 'fi'
    },
    imagePreviewUrl () {
      if (this.pendingImageUrl) {
        return this.pendingImageUrl
      }
      if (this.campaign.image_url) {
        return this.baseDomain + this.campaign.image_url + (this.campaign.updated_at ? '?t=' + this.campaign.updated_at : '')
      }
      return ''
    },
    shopOptions () {
      return this.shops.map(shop => ({
        id: String(shop.shopid),
        name: shop.companyname ? shop.companyname + ' (' + shop.shopid + ')' : String(shop.shopid)
      }))
    },
    // A shop picked in one list is left out of the other
    includeShopItems () {
      return this.shopOptions.filter(shop => this.campaign.targets.exclude_shops.indexOf(shop.id) === -1)
    },
    excludeShopItems () {
      return this.shopOptions.filter(shop => this.campaign.targets.shops.indexOf(shop.id) === -1)
    },
    targetShops: {
      get () {
        return this.shopItems(this.campaign.targets.shops)
      },
      set (items) {
        this.setTargetShops('shops', items)
      }
    },
    excludeShops: {
      get () {
        return this.shopItems(this.campaign.targets.exclude_shops)
      },
      set (items) {
        this.setTargetShops('exclude_shops', items)
      }
    },
    targetEmails () {
      return this.normalizeTargetEmails(this.targetEmailsText)
    },
    invalidTargetEmails () {
      return this.targetEmails.filter(email => !/^([^\s@]+)?@[^\s@]+\.[^\s@]+$/.test(email))
    }
  },
  methods: {
    shopItems (ids) {
      return ids.map(id => this.shopOptions.find(shop => shop.id === id) || { id, name: id })
    },
    setTargetShops (list, items) {
      const ids = (items || []).map(item => String(item.id))
      if (ids.join(',') !== this.campaign.targets[list].join(',')) {
        this.campaign.targets[list] = ids
      }
    },
    // Trimmed, lowercase and unique, one entry per line (commas and semicolons also separate)
    normalizeTargetEmails (text) {
      const out = []
      String(text || '').split(/[\n,;]+/).forEach(line => {
        const email = line.trim().toLowerCase()
        if (email && out.indexOf(email) === -1) {
          out.push(email)
        }
      })
      return out
    },
    emptyTargets () {
      return { shops: [], exclude_shops: [], emails: [] }
    },
    async loadShops () {
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/shops.json?apikey=' + this.apiKey)
        const rows = Array.isArray(data) ? data : (data && typeof data === 'object' ? Object.values(data) : [])
        this.shops = rows.filter(shop => shop && shop.shopid).sort((a, b) => String(a.shopid).localeCompare(String(b.shopid)))
      } catch (e) {
        this.showError(e)
      } finally {
        this.shopsLoaded = true
      }
    },
    emptyCampaign () {
      return {
        id: 0,
        type: 'manual',
        published: 0,
        start_ts: null,
        end_ts: null,
        min_userlevel: DEFAULT_LEVEL,
        priority: 0,
        feature_flag: '',
        feature_flag_exclude: 0,
        source_shopid: '',
        source_category_id: null,
        title_fi: '',
        title_en: '',
        text_fi: '',
        text_en: '',
        cta_title_fi: '',
        cta_title_en: '',
        cta_url_fi: '',
        cta_url_en: '',
        image_url: null,
        has_image: false,
        targets: this.emptyTargets()
      }
    },
    getSaveData () {
      return {
        id: this.campaign.id || 0,
        type: this.campaign.type || 'manual',
        published: this.campaign.published ? 1 : 0,
        start_ts: this.campaign.start_ts || null,
        end_ts: this.campaign.end_ts || null,
        min_userlevel: this.normalizeLevel(this.campaign.min_userlevel),
        feature_flag: this.campaign.feature_flag || '',
        feature_flag_exclude: this.campaign.feature_flag && this.campaign.feature_flag_exclude ? 1 : 0,
        source_shopid: this.isFeed ? (this.campaign.source_shopid || '') : '',
        source_category_id: this.isFeed ? (this.campaign.source_category_id || null) : null,
        title_fi: this.campaign.title_fi || '',
        title_en: this.campaign.title_en || '',
        text_fi: this.campaign.text_fi || '',
        text_en: this.campaign.text_en || '',
        cta_title_fi: this.campaign.cta_title_fi || '',
        cta_title_en: this.campaign.cta_title_en || '',
        cta_url_fi: this.campaign.cta_url_fi || '',
        cta_url_en: this.campaign.cta_url_en || '',
        targets: {
          shops: [...this.campaign.targets.shops],
          exclude_shops: [...this.campaign.targets.exclude_shops],
          emails: this.targetEmails
        }
      }
    },
    previewPayload () {
      const title = this.localizedPreview(this.campaign.title_fi, this.campaign.title_en)
      const text = this.localizedPreview(this.campaign.text_fi, this.campaign.text_en)
      const ctaTitle = this.localizedPreview(this.campaign.cta_title_fi, this.campaign.cta_title_en)
      const ctaUrl = this.localizedPreview(this.campaign.cta_url_fi, this.campaign.cta_url_en)
      return {
        id: this.campaign.id || 0,
        title,
        text,
        image_url: this.imagePreviewUrl,
        published: this.campaign.published,
        preview: true,
        cta: ctaUrl ? {
          type: 'external',
          title: ctaTitle,
          url: ctaUrl
        } : null
      }
    },
    localizedPreview (fi, en) {
      const primary = this.previewLocale === 'fi' ? fi : en
      return primary || fi || en || ''
    },
    onImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      this.pendingImage = file
      if (this.pendingImageUrl) {
        URL.revokeObjectURL(this.pendingImageUrl)
      }
      this.pendingImageUrl = URL.createObjectURL(file)
    },
    async uploadPendingImage (id) {
      if (!this.pendingImage || !id) {
        return
      }
      const formData = new FormData()
      formData.append('files[]', this.pendingImage, this.pendingImage.name)
      const { data } = await axios.post(
        this.baseDomain + this.shopId + '/campaigns/' + id + '/image.json?apikey=' + this.apiKey,
        formData
      )
      this.pendingImage = null
      if (this.pendingImageUrl) {
        URL.revokeObjectURL(this.pendingImageUrl)
        this.pendingImageUrl = ''
      }
      return data
    },
    normalizeLevel (value) {
      const level = parseInt(value, 10)
      return Number.isNaN(level) ? DEFAULT_LEVEL : level
    },
    applyCampaign (data) {
      this.campaign = Object.assign(this.emptyCampaign(), data || {})
      this.campaign.feature_flag = this.campaign.feature_flag || ''
      this.campaign.source_shopid = this.campaign.source_shopid || ''
      // Older backends leave targets out
      const targets = this.campaign.targets || {}
      this.campaign.targets = {
        shops: Array.isArray(targets.shops) ? targets.shops.map(String) : [],
        exclude_shops: Array.isArray(targets.exclude_shops) ? targets.exclude_shops.map(String) : [],
        emails: Array.isArray(targets.emails) ? targets.emails : []
      }
      this.targetEmailsText = this.campaign.targets.emails.join('\n')
      this.campaign.min_userlevel = this.normalizeLevel(this.campaign.min_userlevel)
      this.storeState()
    },
    async loadSourceCategories () {
      const shopId = this.campaign.source_shopid
      if (!this.isFeed || !shopId) {
        this.sourceCategories = []
        return
      }
      this.categoriesLoading = true
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/campaigns/categories.json?apikey=' + this.apiKey + '&shopid=' + encodeURIComponent(shopId))
        if (shopId === this.campaign.source_shopid) {
          this.sourceCategories = Array.isArray(data) ? data : []
        }
      } catch (e) {
        this.sourceCategories = []
      } finally {
        this.categoriesLoading = false
      }
    },
    debouncedLoadSourceCategories: debounce(function () {
      this.loadSourceCategories()
    }, 400),
    async loadCampaign (id) {
      if (!id) {
        this.applyCampaign(this.emptyCampaign())
        return
      }
      this.loading = true
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/campaigns/' + id + '.json?apikey=' + this.apiKey)
        this.applyCampaign(data)
      } catch (e) {
        this.showError(e)
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.saving = true
      const payload = this.getSaveData()
      try {
        let data
        if (payload.id) {
          const response = await axios.put(this.baseDomain + this.shopId + '/campaigns/' + payload.id + '.json?apikey=' + this.apiKey, payload)
          data = response.data
        } else {
          delete payload.id
          const response = await axios.post(this.baseDomain + this.shopId + '/campaigns.json?apikey=' + this.apiKey, payload)
          data = response.data
        }
        if (data && data.id && this.pendingImage) {
          const uploaded = await this.uploadPendingImage(data.id)
          if (uploaded) {
            data = uploaded
          }
        }
        this.applyCampaign(data)
        if (this.component && this.component.initData) {
          this.component.initData.campaignId = data.id
        }
        this.EventBus.$emit('campaignUpdate')
        this.showNotification(this._('Changes saved successfully!'))
        this.saved = true
        setTimeout(() => { this.saved = false }, 1000)
      } catch (e) {
        this.showError(e)
      } finally {
        this.saving = false
      }
    },
    async preview () {
      let item = this.previewPayload()
      if (this.isFeed) {
        if (!this.campaign.id) {
          this.showNotification(this._('Save the campaign before previewing it.'))
          return
        }
        this.loading = true
        try {
          const version = this.previewLocale === 'fi' ? 'fi_FI' : 'en_US'
          const { data } = await axios.get(this.baseDomain + this.shopId + '/campaigns/' + this.campaign.id + '/preview.json?apikey=' + this.apiKey + '&version=' + version)
          if (!data || !data.id) {
            this.showNotification(this._('No published products were found in the category.'))
            return
          }
          if (data.image_url && !/^https?:\/\//.test(data.image_url)) {
            data.image_url = this.baseDomain + data.image_url
          }
          item = data
        } catch (e) {
          this.showError(e)
          return
        } finally {
          this.loading = false
        }
      }
      this.showModal('campaignpreview', CampaignMobilepay, {
        props: {
          items: [item],
          shopId: this.shopId,
          preview: true
        },
        width: 800
      })
    },
    async doRemove () {
      if (!this.campaign.id) {
        this.close()
        return
      }
      this.loading = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/campaigns/' + this.campaign.id + '.json?apikey=' + this.apiKey)
        this.EventBus.$emit('campaignUpdate')
        this.close()
      } catch (e) {
        this.showError(e)
      } finally {
        this.loading = false
      }
    },
    removeCampaign () {
      this.remove(this._('Are you sure? Deletion can\'t be undone.'))
    }
  },
  watch: {
    'campaign.type' () {
      this.loadSourceCategories()
    },
    'campaign.source_shopid' () {
      this.debouncedLoadSourceCategories()
    }
  },
  components: {
    DatePicker,
    DraggableMultiSelector,
    Editor
  },
  created () {
    const id = this.component && this.component.initData ? this.component.initData.campaignId : 0
    this.loadCampaign(id)
    this.loadShops()
  },
  async updated () {
    const requested = this.component && this.component.initData ? parseInt(this.component.initData.campaignId || 0, 10) : 0
    const current = parseInt(this.campaign.id || 0, 10)
    if (current !== requested && !this.loading) {
      await this.loadCampaign(requested)
    }
  }
}
</script>

<style scoped>
.campaign-published {
  padding: 10px 12px;
  margin-bottom: 18px;
  background: #f5f7f8;
  border: 1px solid #d3dbe0;
  border-radius: 4px;
}
.campaign-published label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  font-size: 16px;
}
.campaign-published input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
}
.campaign-image-preview {
  margin-bottom: 10px;
}
.campaign-flag-mode {
  margin-top: 6px;
}
.campaign-image-preview img {
  max-width: 100%;
  height: auto;
}
.campaign-datepicker {
  display: block;
  width: 100%;
}
.campaign-datepicker :deep(.vuedatepicker),
.campaign-datepicker :deep(.dp__main),
.campaign-datepicker :deep(.dp__input_wrap),
.campaign-datepicker :deep(.dp__input),
.campaign-datepicker :deep(.form-control) {
  width: 100%;
  max-width: none;
}
</style>
